import { Typography } from '@mui/material';
import React from 'react';
import {
  ArrayField,
  ArrayInput,
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  Datagrid,
  ReferenceField,
  ReferenceInput,
  ShowButton,
  SimpleFormIterator,
  TextField,
  useRecordContext,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { CoachType } from '../../../graphql/types';

const getIsAssignableCoach = (coachType: CoachType) =>
  [CoachType.Coach, CoachType.Dietitian].includes(coachType);

const AssignmentBooleanField: React.FC<{
  source: string;
  label?: string;
  isAssignableCoach: boolean;
}> = ({ source, label, isAssignableCoach }) => {
  if (!isAssignableCoach) {
    return <span>N/A</span>;
  }
  return <BooleanField source={source} label={label} />;
};

export const ListCoachClients: React.FC = () => {
  const record = useRecordContext();
  const isAssignableCoach = getIsAssignableCoach(record?.coachData?.type);

  return (
    <>
      <Typography variant="h5" fontSize={20}>
        Clients
      </Typography>
      <ArrayField resource="Client" source="coachData.coachClients.edges">
        <Datagrid resource="Client" bulkActionButtons={false} rowClick={false}>
          <TextField source="node.displayName" label="Name" />
          <AssignmentBooleanField
            source="availableForAssignment"
            isAssignableCoach={isAssignableCoach}
          />
          <AssignmentBooleanField
            source="supportsWellbeingCoaching"
            isAssignableCoach={isAssignableCoach}
          />
          <AssignmentBooleanField
            source="supportsHighPerformanceCoaching"
            isAssignableCoach={isAssignableCoach}
          />

          <ReferenceField reference="Client" source="node.id" label="">
            <ShowButton />
          </ReferenceField>
        </Datagrid>
      </ArrayField>
    </>
  );
};

export const EditCoachClients: React.FC = () => {
  const coachType = useWatch({ name: 'coachData.type' });
  const isAssignableCoach = getIsAssignableCoach(coachType);

  return (
    <>
      <Typography variant="h5" fontSize={20}>
        Clients
      </Typography>
      {!isAssignableCoach && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mt: '1em', mb: '1em' }}
        >
          This coach type is not eligible for member assignment.
        </Typography>
      )}
      {coachType === CoachType.Dietitian && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mt: '1em', mb: '1em' }}
        >
          Nutrition specialists cannot be assigned to members. Instead, these
          toggles control whether they are visible in-app.
        </Typography>
      )}
      <ArrayInput
        resource="Client"
        source="coachData.coachClients.edges"
        label=""
        validate={(values) => {
          if (
            values.some(
              (value: { node: { id: string | undefined } }) => !value.node.id,
            )
          ) {
            return 'All rows must have a client selected. Select a client or remove any empty rows.';
          }
          return undefined;
        }}
        data-testid="coach-clients-array-input"
      >
        <SimpleFormIterator inline disableReordering>
          <ReferenceInput
            source="node.id"
            reference="Client"
            filterToQuery={(name: string) => ({
              name,
            })}
          >
            <AutocompleteInput
              label="Name"
              optionText="name"
              filter="name"
              sx={{
                minWidth: '300px',
              }}
              data-testid="client-autocomplete"
            />
          </ReferenceInput>
          {isAssignableCoach && (
            <>
              <BooleanInput
                source="availableForAssignment"
                sx={{ alignSelf: 'center' }}
              />
              <BooleanInput
                source="supportsWellbeingCoaching"
                sx={{ alignSelf: 'center' }}
                data-testid="supports-wellbeing-coaching"
              />
              <BooleanInput
                source="supportsHighPerformanceCoaching"
                sx={{ alignSelf: 'center' }}
              />
            </>
          )}
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};
